<template>
  <div class="columns is-multiline">
    <div class="column is-10 is-offset-2">
      <b-field :label="$t(getLabelObjet)"
               custom-class="has-text-default has-text-right">
        <b-input v-model="facture.theme"></b-input>
      </b-field>
    </div>
    <div class="column is-5 is-offset-2">
      <b-field :label="$t('editionCreationDepenseBailleur.depenseBailleur.codeDossier')"
               custom-class="has-text-default has-text-right">
        <b-input v-model="facture.codeDossier"></b-input>
      </b-field>
    </div>
    <div class="column is-5">
      <b-field :label="$t(getLabelDate)"
               class="obligatoire"
               custom-class="has-text-default has-text-right">
        <div class="column is-12 is-paddingless">
          <div class="columns">
            <b-tooltip class="width-100percent"
              :label="errorsExercice"
              multiline
              type="is-warning"
              position="is-left"
              v-if="!exerciceOk">
              <b-icon icon="alert" custom-size="mdi-24px" type="is-warning"
                class="column is-2"
                v-if="!exerciceOk"></b-icon>
            </b-tooltip>
            <b-datepicker show-week-number
              icon="calendar-today"
              :editable="true"
              position="is-bottom-left"
              @input="dateChanged"
              class="column"
              :class="exerciceOk?'is-12':'is-10'"
              v-model="facture.dateDevisCommande"
              :nearby-selectable-month-days="true">
            </b-datepicker>
          </div>
        </div>
      </b-field>
    </div>
    <div class="column is-5 is-offset-2">
      <b-field :label="$t(getLabelReference)"
        custom-class="has-text-default has-text-right">
        <b-input v-model="facture.code" disabled></b-input>
      </b-field>
    </div>
    <div class="column is-5">
      <b-field :label="$t('editionCreationDepenseBailleur.depenseBailleur.referenceFournisseur')"
               custom-class="has-text-default has-text-right">
        <b-input v-model="facture.referenceClient"></b-input>
      </b-field>
    </div>
    <div
      v-if="moduleLogistiqueImpression && isModuleAchat && !isBonReception && isCommande"
      class="column is-5 is-offset-7">
      <b-field
        class="has-text-right"
        :label="$t('addEditCommande.informations.labelReferenceExterne')">
        <b-autocomplete
          icon="magnify"
          v-model="referenceExterneSelected"
          :disabled="hasBesoinLies"
          field="code"
          :data="dataCommande"
          :loading="isFetchingCommande"
          @input="getCommandeAsync()"
          @select="selectCommande">
          <template #default="{ option }">
            <div class="has-text-left">{{option.code}}</div>
          </template>
        </b-autocomplete>
      </b-field>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import debounce from 'lodash/debounce';
import { getAll } from '@/api/commande';
import { getAll as getAllBesoin } from '@/api/besoin';
import { checkExercice } from '@/api/compta/exerciceComptable/index';
import validationEcriture from '@/services/ecritureComptable/validation';
import { MODULE_TLPE } from '@/configuration/gabed/titreRecette/titreRecetteConf';
import {
  MODULE_ACHAT,
  SENS_VENTE,
  SOUS_TYPE_BON_RECEPTION,
  SOUS_TYPE_NORMAL,
  TYPE_COMMANDE,
  COMMANDE_ETAT,
  ETAT_SANSSUITE,
} from '@/configuration/devisConf';
import messageErreur from '@/services/general/messageErreur';
import { TYPE_REFERENCE_EXTERNE_VENTE } from '@/configuration/dcf/dcfConf';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';

const Societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');

export default {
  props: {
    facture: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      exerciceOk: true,
      errorsExercice: '',
      referenceExterneSelected: null,
      dataCommande: [],
      isFetchingCommande: false,
      hasBesoinLies: false,
    };
  },
  methods: {
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    dateChanged: debounce(async function dateDealer() {
      await checkExercice(this.societeActiveId, this.facture.dateDevisCommande)
        .then(response => {
          this.exerciceOk = response.data.result.state;
          if (response.data.result.errors !== null && response.data.result.errors.length >= 1) {
            const messages = [];
            validationEcriture(this.$i18n, response.data.result.errors, messages);
            this.errorsExercice = this.$i18n.t(messages[0].message, messages[0].parametres);
          }
        });
    }),
    getCommandeAsync: debounce(async function loadData() {
      if (!this.referenceExterneSelected) {
        this.facture.referenceExterne = null;
        return;
      }
      this.isFetchingCommande = true;
      try {
        const result = await getAll({
          query:
            {
              code: this.referenceExterneSelected,
              societeId: this.societeActiveId,
              sousType: [SOUS_TYPE_NORMAL],
              type: TYPE_COMMANDE,
              sens: SENS_VENTE,
              etat: COMMANDE_ETAT.filter(e => e.code !== ETAT_SANSSUITE).map(e => e.code),
            },
        });
        this.dataCommande = result.data.result.items;
      } catch (e) {
        messageErreur(this.$i18n);
        throw (e);
      } finally {
        this.isFetchingCommande = false;
      }
    }, 500),
    selectCommande(commande) {
      this.facture.referenceExterne = commande?.id;
    },
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    moduleLogistiqueImpression() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleLogistiqueImpression.key);
    },
    isModuleAchat() {
      return this.facture.module === MODULE_ACHAT;
    },
    isBonReception() {
      return this.facture.sousType === SOUS_TYPE_BON_RECEPTION;
    },
    isCommande() {
      return this.facture.type === TYPE_COMMANDE;
    },
    getLabelObjet() {
      let label = '';
      switch (this.facture.module) {
        case MODULE_TLPE:
          label = 'editionTitreRecette.titreRecette.objetTitre';
          break;
        case MODULE_ACHAT:
          if (this.facture.sousType === SOUS_TYPE_BON_RECEPTION) {
            label = 'addEditBonReception.informations.labelObjet';
          } else label = 'addEditCommande.informations.labelObjet';
          break;
        default:
          label = 'editionCreationDepenseBailleur.depenseBailleur.objetFacture';
          break;
      }
      return label;
    },
    getLabelDate() {
      let label = '';
      switch (this.facture.module) {
        case MODULE_TLPE:
          label = 'editionTitreRecette.titreRecette.dateDepense';
          break;
        case MODULE_ACHAT:
          if (this.facture.sousType === SOUS_TYPE_BON_RECEPTION) {
            label = 'addEditBonReception.informations.labelDate';
          } else label = 'addEditCommande.informations.labelDate';
          break;
        default:
          label = 'editionCreationDepenseBailleur.depenseBailleur.dateDepense';
          break;
      }
      return label;
    },
    getLabelReference() {
      let label = '';
      switch (this.facture.module) {
        case MODULE_TLPE:
          label = 'editionTitreRecette.titreRecette.referenceDepense';
          break;
        case MODULE_ACHAT:
          if (this.facture.sousType === SOUS_TYPE_BON_RECEPTION) {
            label = 'addEditBonReception.informations.labelReference';
          } else label = 'addEditCommande.informations.labelReference';
          break;
        default:
          label = 'editionCreationDepenseBailleur.depenseBailleur.referenceDepense';
          break;
      }
      return label;
    },
  },
  async mounted() {
    await checkExercice(this.societeActiveId, this.facture.dateDevisCommande)
      .then(response => {
        this.exerciceOk = response.data.result.state;
        if (response.data.result.errors !== null && response.data.result.errors.length >= 1) {
          const messages = [];
          validationEcriture(this.$i18n, response.data.result.errors, messages);
          this.errorsExercice = this.$i18n.t(messages[0].message, messages[0].parametres);
        }
      });
    await this.loadParametresInterne([PARAMETRES_INTERNE_KEYS.moduleLogistiqueImpression.key]);
    try {
      if (
        !!this.facture.referenceExterne
        && this.facture.typeReferenceExterne === TYPE_REFERENCE_EXTERNE_VENTE
      ) {
        this.isFetchingCommande = true;
        const [commande, besoins] = await Promise.all([
          (await getAll({
            query: {
              ids: [this.facture.referenceExterne],
              societeId: this.societeActiveId,
              sousType: [SOUS_TYPE_NORMAL],
              type: TYPE_COMMANDE,
              sens: SENS_VENTE,
            },
          })).data.result.items,
          (await getAllBesoin({
            query: {
              commandeAchatId: this.facture.id,
            },
          })).data.result.totalCount,
        ]);
        this.referenceExterneSelected = commande.pop().code;
        this.hasBesoinLies = besoins > 0;
      }
    } catch (e) {
      messageErreur(this.$i18n);
      throw (e);
    } finally {
      this.isFetchingCommande = false;
    }
  },
};
</script>
